import { BookOpen, ChevronDown, CircleHelp, ListChecks, PlayCircle, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { Link, useLocation } from "react-router-dom";
import { getHelpContext, helpUiCopy, type WalkthroughStep } from "../helpContent";
import { useI18n } from "../i18n";

export function HelpAssistant({ onStartSetupGuide }: { onStartSetupGuide?: (() => void) | undefined }) {
  const { language } = useI18n();
  const location = useLocation();
  const ui = helpUiCopy[language];
  const context = useMemo(() => getHelpContext(location.pathname, language), [language, location.pathname]);
  const [open, setOpen] = useState(false);
  const [tourSteps, setTourSteps] = useState<WalkthroughStep[]>([]);
  const [tourStep, setTourStep] = useState<number | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    closeButtonRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => { if (event.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open]);

  useEffect(() => { setOpen(false); setTourStep(null); }, [location.pathname]);

  const startTour = () => {
    const available = context.walkthrough.filter((step) => document.querySelector(`[data-help-id="${step.target}"]`));
    if (!available.length) return;
    setTourSteps(available);
    setTourStep(0);
    setOpen(false);
  };

  return <>
    <button className="help-trigger" type="button" aria-expanded={open} aria-controls="context-help-panel" onClick={() => setOpen(true)}>
      <CircleHelp size={20} /><span>{ui.help}</span>
    </button>
    {open && <div className="help-layer">
      <button className="help-dismiss-area" type="button" aria-label={ui.close} onClick={() => setOpen(false)} />
      <aside id="context-help-panel" className="help-panel" role="dialog" aria-modal="false" aria-labelledby="help-panel-title">
        <header className="help-panel-header"><div><p className="help-eyebrow">{context.eyebrow}</p><h2 id="help-panel-title">{context.title}</h2></div><button ref={closeButtonRef} className="icon-button" type="button" onClick={() => setOpen(false)} aria-label={ui.close}><X size={20} /></button></header>
        <div className="help-panel-body">
          <p className="help-summary">{context.summary}</p>
          <div className="help-sections">{context.sections.map((section, index) => <details key={section.title} open={index === 0} className={`help-section help-section-${section.tone}`}><summary><span><small>{section.badge}</small><strong>{section.title}</strong></span><ChevronDown size={19} /></summary><div><p>{section.body}</p>{section.bullets.length > 0 && <ul>{section.bullets.map((bullet) => <li key={bullet}>{bullet}</li>)}</ul>}</div></details>)}</div>
          {context.walkthrough.length > 0 && <button className="button button-primary button-full help-tour-button" type="button" onClick={startTour}><PlayCircle size={19} />{ui.showTour}</button>}
          <nav className="help-panel-links" aria-label={ui.help}>
            <Link to="/help" onClick={() => setOpen(false)}><BookOpen size={18} />{ui.allTopics}</Link>
            {onStartSetupGuide && <button type="button" onClick={() => { setOpen(false); onStartSetupGuide(); }}><ListChecks size={18} />{ui.setupGuide}</button>}
          </nav>
        </div>
      </aside>
    </div>}
    {tourStep != null && tourSteps[tourStep] && <TourOverlay step={tourSteps[tourStep]} stepNumber={tourStep + 1} total={tourSteps.length} onClose={() => setTourStep(null)} onNext={() => tourStep === tourSteps.length - 1 ? setTourStep(null) : setTourStep(tourStep + 1)} />}
  </>;
}

function TourOverlay({ step, stepNumber, total, onClose, onNext }: { step: WalkthroughStep; stepNumber: number; total: number; onClose: () => void; onNext: () => void }) {
  const { language } = useI18n();
  const ui = helpUiCopy[language];
  const [rect, setRect] = useState<DOMRect | null>(null);

  useEffect(() => {
    const element = document.querySelector(`[data-help-id="${step.target}"]`);
    if (!(element instanceof HTMLElement)) return;
    element.scrollIntoView({ behavior: "smooth", block: "center" });
    const update = () => setRect(element.getBoundingClientRect());
    const timer = window.setTimeout(update, 220);
    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => { window.clearTimeout(timer); window.removeEventListener("resize", update); window.removeEventListener("scroll", update, true); };
  }, [step.target]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => { if (event.key === "Escape") onClose(); if (event.key === "ArrowRight" || event.key === "Enter") onNext(); };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose, onNext]);

  const spotlightStyle: CSSProperties | undefined = rect ? { top: rect.top - 8, left: rect.left - 8, width: rect.width + 16, height: rect.height + 16 } : undefined;
  const coachStyle: CSSProperties = rect && window.innerWidth > 760 ? { top: rect.bottom + 238 < window.innerHeight ? rect.bottom + 18 : Math.max(16, rect.top - 222), left: Math.min(Math.max(16, rect.left), window.innerWidth - 356) } : {};

  return <div className="tour-layer" role="dialog" aria-label={`${ui.step} ${stepNumber} ${ui.of} ${total}`}>
    {rect && <div className="tour-spotlight" style={spotlightStyle} />}
    <section className="tour-coachmark" style={coachStyle}><div className="tour-coachmark-top"><span>{ui.step} {stepNumber} {ui.of} {total}</span><button type="button" onClick={onClose} aria-label={ui.tourClose}><X size={18} /></button></div><h3>{step.title}</h3><p>{step.body}</p><button className="button button-primary button-full" type="button" onClick={onNext}>{stepNumber === total ? ui.tourDone : ui.tourNext}</button></section>
  </div>;
}
